<ng-template #unpauseLine>
  @if (activeGame?.UnpauseOn) {
    <p data-testid="game-state-unpause-date">It will unpause automatically on {{ activeGame?.UnpauseOn | date: 'yyyy-MM-dd h:mm a' }}.</p>
  } @else {
    <p data-testid="game-state-unpause-indefinite">It will stay paused until an admin unpauses it.</p>
  }
</ng-template>

<mat-card>
  <mat-card-header>
    <mat-card-title>Game State Settings</mat-card-title>
  </mat-card-header>

  <mat-card-content>
    <div data-testid="game-state-status">
      @switch (activeGame!.State) {
        @case (gameStateEnum.Active) {
          <p data-testid="game-state-active">{{ activeGame?.Name }} is active. Draws will proceed as normal.</p>
        }
        @case (gameStateEnum.Inactive) {
          <p data-testid="game-state-inactive">{{ activeGame?.Name }} is inactive. No more draws will take place.</p>
        }
        @case (gameStateEnum.Paused) {
          <div data-testid="game-state-paused">
            <p>{{ activeGame?.Name }} is paused.</p>
            <ng-container *ngTemplateOutlet="unpauseLine"></ng-container>
          </div>
        }
        @case (gameStateEnum.NoLicense) {
          <div data-testid="game-state-no-licence">
            <p>{{ activeGame?.Name }} is stopped: no lottery licence covers its next draw, so no draw can run.</p>
            <p>It resumes on its own once a licence covers it. To keep it paused after that, pause it before adding the licence.</p>
          </div>
        }
        @case (gameStateEnum.PauseAfterNextDraw) {
          <div data-testid="game-state-pause-after-next-draw">
            <p>{{ activeGame?.Name }} is currently active, but will be paused after the draw on {{ activeDraw()?.EndedOn | date: 'yyyy-MM-dd h:mm a' }}.</p>
            <ng-container *ngTemplateOutlet="unpauseLine"></ng-container>
          </div>
        }
        @case (gameStateEnum.PauseAfterNextWinner) {
          <div data-testid="game-state-pause-after-next-winner">
            <p>{{ activeGame?.Name }} is currently active, but will be paused the next time a player wins a draw.</p>
            <ng-container *ngTemplateOutlet="unpauseLine"></ng-container>
          </div>
        }
        @case (gameStateEnum.PauseAfterNextDrawCancelAutoplays) {
          <div data-testid="game-state-pause-after-next-draw-cancel-autoplays">
            <p>{{ activeGame?.Name }} is currently active, but will be paused after the draw on {{ activeDraw()?.EndedOn | date: 'yyyy-MM-dd h:mm a' }}.</p>
            <ng-container *ngTemplateOutlet="unpauseLine"></ng-container>
            <p>All autoplays will be cancelled when the game is paused.</p>
          </div>
        }
        @case (gameStateEnum.PauseAfterNextWinnerCancelAutoplays) {
          <div data-testid="game-state-pause-after-next-winner-cancel-autoplays">
            <p>{{ activeGame?.Name }} is currently active, but will be paused the next time a player wins a draw.</p>
            <ng-container *ngTemplateOutlet="unpauseLine"></ng-container>
            <p>All autoplays will be cancelled when the game is paused.</p>
          </div>
        }
        @case (gameStateEnum.EndAfterNextDraw) {
          <p data-testid="game-state-end-after-next-draw">{{ activeGame?.Name }} is active, but will end after the draw on {{ activeDraw()?.EndedOn | date: 'yyyy-MM-dd h:mm a' }}.</p>
        }
        @case (gameStateEnum.EndAfterNextWinner) {
          <p data-testid="game-state-end-after-next-winner">{{ activeGame?.Name }} is active, but will end the next time a player wins a draw.</p>
        }
      }
    </div>
    @if (activeGame?.Type === gameTypeEnum.GoldRush)
    {
      <!-- ForceDrawWinner, not ModifyGameSettings: that is what the force-draw-winner endpoint requires. -->
      <div class="flex-row justify-space-between align-center custom-border-bottom" *hasPermission="permissionTypes.ForceDrawWinner">
        <div class="flex-66">
          @if (uiState === uiStateEnum.ShowLoading)
          {
            <mat-spinner diameter="24"></mat-spinner>
          } @else {
            <mat-slide-toggle
              data-testid="force-winner-toggle"
              [checked]="activeDraw()?.ForceWinner ?? false"
              [disabled]="!activeDraw()"
              (change)="updateGameShouldForceWinnerStatus()">
              @if (activeDraw()) {
                Force a winner in the current draw (Draw date: {{activeDraw()?.EndedOn | date: 'yyyy-MM-dd h:mm a'}})
              } @else {
                <span data-testid="force-winner-no-draw">Force a winner in the current draw (no draw is currently running)</span>
              }
            </mat-slide-toggle>
            @if (activeDraw()) {
              <p class="force-winner-hint" data-testid="force-winner-hint">
                On: the winning number is drawn from the tickets played in this draw, so the draw always produces a
                winner. Off: it is drawn from every number in the game and can land on one nobody bought.
              </p>
            }
          }
        </div>
      </div>
    }
    <div class="margin-top-med">
      <div class="flex-row">
        @if (!isGamePausedOrPendingPause()) {
          <button mat-raised-button
                  data-testid="pause-game-button"
                  *hasPermission="permissionTypes.PauseGameInstance"
                  color="primary"
                  (click)="openPauseGame()"
                  matTooltip="Prevents new draws until the unpause date"
                  matTooltipShowDelay="500">Pause Game
            <mat-icon>pause_circle</mat-icon>
          </button>
        } @else {
          @if (activeGame?.State === gameStateEnum.NoLicense) {
          <button mat-raised-button
                  class="margin-right-small"
                  data-testid="pause-no-licence-game-button"
                  *hasPermission="permissionTypes.PauseGameInstance"
                  color="primary"
                  (click)="openPauseGame()"
                  matTooltip="Keep the game paused until a date, even after a licence is added. Pause before adding the licence."
                  matTooltipShowDelay="500">Pause Game
            <mat-icon>pause_circle</mat-icon>
          </button>
          } @else {
          <button mat-raised-button
                  class="margin-right-small"
                  data-testid="change-unpause-date-button"
                  *hasPermission="permissionTypes.PauseGameInstance"
                  color="primary"
                  (click)="openChangeUnpauseDate()"
                  matTooltip="Change when the game will automatically unpause"
                  matTooltipShowDelay="500">Change Unpause Date
            <mat-icon>schedule</mat-icon>
          </button>
          }
          <button mat-raised-button
                  data-testid="unpause-game-button"
                  *hasPermission="permissionTypes.PauseGameInstance"
                  color="primary"
                  (click)="openUnpauseGame()"
                  [matTooltip]="activeGame?.State === gameStateEnum.NoLicense
                    ? 'Resume the game now. Only possible once a lottery licence covers it.'
                    : activeGame?.State === gameStateEnum.Paused
                      ? 'Return the game to active now. Draws resume on the next scheduled run.'
                      : 'Cancel the scheduled pause. Draws continue as normal.'"
                  matTooltipShowDelay="500">{{ activeGame?.State === gameStateEnum.Paused || activeGame?.State === gameStateEnum.NoLicense ? 'Unpause Game' : 'Cancel Scheduled Pause' }}
            <mat-icon>play_circle</mat-icon>
          </button>
        }
      </div>
    </div>
    <div class="margin-top-med">
      <div class="flex-row">
        @if (activeGame?.State !== gameStateEnum.NoLicense) {
        <button mat-raised-button
                data-testid="stop-game-button"
                *hasPermission="permissionTypes.StopGame"
                color="primary"
                (click)="openStopGame()"
                matTooltip="Prevents new draws and cancels all active autoplays. Cannot be undone."
                matTooltipShowDelay="500">Stop Game
          <mat-icon>cancel</mat-icon>
        </button>
        }
      </div>
    </div>
    <div class="margin-top-med">
      <div class="flex-row">
        <!-- Ending refunds the running draw's tickets, so with no draw there is nothing to end. The reason sits
             on the card below because a disabled button swallows its own pointer events. -->
        <button mat-raised-button
                data-testid="end-game-button"
                *hasPermission="permissionTypes.EndGame"
                [disabled]="!activeDraw()"
                (click)="openEndGame()"
                matTooltip="End game immediately and refund all current tickets"
                matTooltipShowDelay="500"
                color="warn">End Game
          <mat-icon>warning</mat-icon>
        </button>
      </div>
      <!-- Only once the fetch has settled: in flight there is no draw either, and saying so would be wrong. -->
      @if (activeDrawLoaded() && !activeDraw()) {
        <p class="no-draw-hint" data-testid="end-game-no-draw-hint" *hasPermission="permissionTypes.EndGame">
          No draw is currently running, so there are no tickets to refund and nothing to end.
        </p>
      }
    </div>
  </mat-card-content>
</mat-card>
